import { useEffect, useId, useState, type FormEvent } from 'react';
import { formatDate, formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, Select, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { addDays, todayKey } from '../../tasks/dates';
import { cancelInvoice, completeInvoice, issueInvoice, useAfterSalesHandover, useStageOptions } from '../api';
import { FieldBlock, FlowChip, RequirementRow } from '../dialogs/bits';
import { NextStepFields, useNextStepForm } from '../dialogs/NextStepFields';
import type { CompleteInvoiceResponse, InvoiceRow } from '../types';
import styles from '../dialogs/dialogs.module.css';

/**
 * Controlling 06 row actions: „Rechnung stellen" (number, date, due date → `/issue`), „Eingang erfassen"
 * (Deal-Pipeline 22 with „Übergabe an" → `/complete`) and „Stornieren" (reason → `/cancel`).
 */

export interface IssueInvoiceDialogProps {
  open: boolean;
  /** The due invoices to choose from; a single one is preselected. */
  candidates: InvoiceRow[];
  preselectedId: string | null;
  onClose: () => void;
  onDone: (row: InvoiceRow) => void;
}

export function IssueInvoiceDialog({
  open,
  candidates,
  preselectedId,
  onClose,
  onDone,
}: IssueInvoiceDialogProps) {
  const formId = useId();
  const [invoiceId, setInvoiceId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoicedOn, setInvoicedOn] = useState(todayKey());
  const [dueOn, setDueOn] = useState(addDays(todayKey(), 14));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInvoiceId(preselectedId ?? candidates[0]?.id ?? '');
    setInvoiceNumber('');
    setInvoicedOn(todayKey());
    setDueOn(addDays(todayKey(), 14));
    setErrors({});
    setBanner(null);
  }, [open, preselectedId, candidates]);

  const row = candidates.find((candidate) => candidate.id === invoiceId) ?? null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const next: Record<string, string> = {};
    if (invoiceId === '') next['invoiceId'] = 'Bitte wählen Sie einen Deal.';
    if (invoiceNumber.trim() === '') next['invoiceNumber'] = 'Bitte eine Rechnungsnummer angeben.';
    if (invoicedOn === '') next['invoicedOn'] = 'Pflichtangabe.';
    if (dueOn === '') next['dueOn'] = 'Pflichtangabe.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      onDone(await issueInvoice(invoiceId, { invoiceNumber: invoiceNumber.trim(), invoicedOn, dueOn }));
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0)
        setErrors(error.fields);
      else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="info"
      title="Rechnung stellen"
      subtitle={row ? `${row.dealLabel} · ${row.developerName ?? ''}` : 'Provision gegenüber dem Bauträger'}
      className={styles.stageDialog}
      initialFocus="first"
      footerNote="Die Task „Rechnung stellen“ wird damit erledigt"
      footer={
        <>
          <Button variant="secondary" className={styles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="dark"
            className={styles.footBtn}
            loading={pending}
            disabled={candidates.length === 0}
          >
            Rechnung stellen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {candidates.length === 0 ? <Banner tone="neutral">Es ist keine Rechnung fällig.</Banner> : null}
        {candidates.length > 1 || !preselectedId ? (
          <Select
            label="Deal"
            size="compact"
            value={invoiceId}
            error={errors['invoiceId']}
            options={candidates.map((candidate) => ({
              value: candidate.id,
              label: `${candidate.dealLabel} · ${formatEuro(candidate.commissionCents)}`,
            }))}
            onChange={(event) => setInvoiceId(event.target.value)}
          />
        ) : null}
        {row ? (
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <div className={styles.sectionLabel}>Kaufpreis</div>
              <div className={styles.tileValue}>
                {row.priceCents === null ? '—' : formatEuro(row.priceCents)}
              </div>
            </div>
            <div className={styles.tile}>
              <div className={styles.sectionLabel}>Provision</div>
              <div className={styles.tileValue}>{formatEuro(row.commissionCents)}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.sectionLabel}>Beurkundet</div>
              <div className={styles.tileText}>{row.notarizedOn ? formatDate(row.notarizedOn) : '—'}</div>
            </div>
          </div>
        ) : null}
        <FieldBlock
          label="Rechnungsnummer"
          note="Pflichtfeld"
          error={errors['invoiceNumber']}
          htmlFor={`${formId}-number`}
        >
          <input
            id={`${formId}-number`}
            className={clsx(styles.input, errors['invoiceNumber'] && styles.inputInvalid)}
            value={invoiceNumber}
            onChange={(event) => setInvoiceNumber(event.target.value)}
          />
        </FieldBlock>
        <div className={clsx(styles.nextStepGrid, styles.nextStepGridTwo)}>
          <FieldBlock
            label="Rechnungsdatum"
            note="Pflichtfeld"
            error={errors['invoicedOn']}
            htmlFor={`${formId}-on`}
          >
            <input
              id={`${formId}-on`}
              type="date"
              className={clsx(styles.input, errors['invoicedOn'] && styles.inputInvalid)}
              value={invoicedOn}
              onChange={(event) => setInvoicedOn(event.target.value)}
            />
          </FieldBlock>
          <FieldBlock label="Fällig am" note="Pflichtfeld" error={errors['dueOn']} htmlFor={`${formId}-due`}>
            <input
              id={`${formId}-due`}
              type="date"
              className={clsx(styles.input, errors['dueOn'] && styles.inputInvalid)}
              value={dueOn}
              min={invoicedOn}
              onChange={(event) => setDueOn(event.target.value)}
            />
          </FieldBlock>
        </div>
      </form>
    </Dialog>
  );
}

export interface ReceivedDialogProps {
  open: boolean;
  row: InvoiceRow | null;
  /** „setzt: <Rolle>" of the handover stage (pipeline settings), when known. */
  responsibleLabel: string | null;
  onClose: () => void;
  onDone: (result: CompleteInvoiceResponse & { ownerName: string }) => void;
}

const HANDOVER_REQUIRED = 'Bitte wählen Sie, an wen der Deal übergeben wird.';

/**
 * Deal-Pipeline 22 „Deal abschließen" (lifts D-64 „Übergabe an display only"): commission receipt, the required
 * after-sales owner „Übergabe an" and the move to the handover stage in one call
 * (`POST /deals/invoices/:id/complete`). Without an open step of the contact the server answers 400 `nextStep`;
 * the dialog then shows the next-step fields and sends again.
 */
export function ReceivedDialog({ open, row, responsibleLabel, onClose, onDone }: ReceivedDialogProps) {
  const formId = useId();
  const handover = useAfterSalesHandover(open && row ? row.dealId : null);
  const [receivedAt, setReceivedAt] = useState(todayKey());
  const [handoverToId, setHandoverToId] = useState('');
  const [errors, setErrors] = useState<{
    receivedAt?: string | undefined;
    handoverToId?: string | undefined;
    nextStep?: string | undefined;
  }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [needsNextStep, setNeedsNextStep] = useState(false);
  const nextStep = useNextStepForm('task');
  const stageOptions = useStageOptions(needsNextStep && row ? row.dealId : null);
  const openStep = stageOptions.data?.openStep ?? null;

  useEffect(() => {
    if (!open) return;
    setReceivedAt(todayKey());
    setHandoverToId('');
    setErrors({});
    setBanner(null);
    setNeedsNextStep(false);
    nextStep.reset({ kind: 'task' });
    // the form resets when the dialog opens for another invoice
  }, [open, row?.id]);

  const defaultId = handover.data?.defaultId ?? null;
  useEffect(() => {
    if (open && defaultId !== null) setHandoverToId((current) => (current === '' ? defaultId : current));
  }, [open, defaultId]);

  useEffect(() => {
    if (openStep !== null) nextStep.setKind('existing');
    // follows the availability of the open step, not every render of the form
  }, [openStep]);

  const candidates = handover.data?.candidates ?? [];
  const chosen = candidates.find((candidate) => candidate.userId === handoverToId) ?? null;
  const stage = handover.data?.stage ?? null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!row || pending) return;
    const next: typeof errors = {};
    if (receivedAt === '') next.receivedAt = 'Pflichtangabe.';
    if (handoverToId === '') next.handoverToId = HANDOVER_REQUIRED;
    const step = needsNextStep ? nextStep.build() : null;
    if (needsNextStep && step === null) next.nextStep = 'Bitte legen Sie den nächsten Schritt fest.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      const result = await completeInvoice(row.id, {
        receivedAt,
        handoverToId,
        ...(step ? { nextStep: step } : {}),
      });
      onDone({ ...result, ownerName: chosen?.label.split(' · ')[0] ?? '' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && Object.keys(err.fields).length > 0) {
        if (err.fields['nextStep']) setNeedsNextStep(true);
        setErrors({
          receivedAt: err.fields['receivedAt'],
          handoverToId: err.fields['handoverToId'],
          nextStep: err.fields['nextStep'],
        });
      } else setBanner(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="success"
      title="Deal abschließen"
      subtitle={row ? `${row.contactName} · ${row.dealLabel.split(' · ').slice(1).join(' · ')}` : ''}
      className={styles.stageDialog}
      initialFocus="container"
      footerNote="Provision wird nach Bestätigung abrechenbar"
      footer={
        <>
          <Button variant="secondary" className={styles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="dark"
            className={styles.footBtn}
            loading={pending}
            disabled={!row || handover.isPending}
          >
            Abschluss bestätigen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {handover.isError ? <Banner tone="danger">{errorMessage(handover.error)}</Banner> : null}
        {row ? (
          <>
            {stage ? (
              <FlowChip
                from="Provisionseingang"
                to={stage.name}
                arrow="→"
                tone="success"
                meta={`setzt: ${responsibleLabel ?? 'Vertriebskopf'}`}
              />
            ) : null}
            <div className={styles.tiles}>
              <div className={styles.tile}>
                <div className={styles.sectionLabel}>Kaufpreis</div>
                <div className={styles.tileValue}>
                  {row.priceCents === null ? '—' : formatEuro(row.priceCents)}
                </div>
              </div>
              <div className={styles.tile}>
                <div className={styles.sectionLabel}>Provision</div>
                <div className={styles.tileValue}>{formatEuro(row.commissionCents)}</div>
              </div>
              <div className={styles.tile}>
                <label htmlFor={`${formId}-received`} className={styles.sectionLabel}>
                  Eingang
                </label>
                <input
                  id={`${formId}-received`}
                  type="date"
                  className={clsx(styles.input, errors.receivedAt && styles.inputInvalid)}
                  style={{ marginTop: 5, minHeight: 34, padding: '7px 10px' }}
                  value={receivedAt}
                  aria-invalid={errors.receivedAt ? true : undefined}
                  onChange={(event) => setReceivedAt(event.target.value)}
                />
                {errors.receivedAt ? (
                  <div className={styles.fieldError} role="alert">
                    {errors.receivedAt}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={styles.listTight}>
              <RequirementRow
                state="met"
                label="Rechnung gestellt"
                detail={`${row.invoiceNumber ?? ''}${row.invoicedOn ? ` · ${formatDate(row.invoicedOn)}` : ''}`}
                owner="Buchhaltung"
              />
              <RequirementRow
                state="met"
                label="Provision eingefroren"
                detail={`${formatEuro(row.commissionCents)}${row.notarizedOn ? ` · Beurkundet ${formatDate(row.notarizedOn)}` : ''}`}
                owner="Vertriebskopf"
              />
              {chosen ? (
                <RequirementRow
                  state="met"
                  label="After-Sales-Zuständigkeit"
                  detail={chosen.label}
                  owner="Admin"
                />
              ) : (
                <RequirementRow
                  state="warning"
                  label="After-Sales-Zuständigkeit offen"
                  detail="kein Betreuer zugewiesen"
                  owner="Admin"
                />
              )}
            </div>
            <FieldBlock label="Übergabe an" note="Pflichtfeld" error={errors.handoverToId}>
              {handover.isPending ? (
                <Skeleton variant="block" height={38} />
              ) : (
                <Select
                  label="Übergabe an"
                  labelHidden
                  size="compact"
                  placeholder="Bitte wählen"
                  value={handoverToId}
                  invalid={errors.handoverToId !== undefined}
                  options={candidates.map((candidate) => ({
                    value: candidate.userId,
                    label: candidate.label,
                  }))}
                  onChange={(event) => setHandoverToId(event.target.value)}
                />
              )}
            </FieldBlock>
            {needsNextStep ? (
              <NextStepFields
                form={nextStep}
                error={errors.nextStep}
                openStep={openStep}
                kinds={['follow_up', 'task']}
                disabled={pending}
              />
            ) : null}
          </>
        ) : null}
      </form>
    </Dialog>
  );
}

export interface CancelInvoiceDialogProps {
  open: boolean;
  row: InvoiceRow | null;
  onClose: () => void;
  onDone: (row: InvoiceRow) => void;
}

export function CancelInvoiceDialog({ open, row, onClose, onDone }: CancelInvoiceDialogProps) {
  const formId = useId();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    setBanner(null);
  }, [open, row?.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!row || pending) return;
    if (reason.trim() === '') {
      setError('Bitte geben Sie einen Grund an.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      onDone(await cancelInvoice(row.id, reason.trim()));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.fields['reason'])
        setError(err.fields['reason']);
      else setBanner(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="danger"
      title="Rechnung stornieren"
      subtitle={row ? `${row.dealLabel}${row.invoiceNumber ? ` · ${row.invoiceNumber}` : ''}` : ''}
      className={styles.stageDialog}
      initialFocus="first"
      footerNote="Die Task „Rechnung stellen“ wird geschlossen"
      footer={
        <>
          <Button variant="secondary" className={styles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="destructiveFilled"
            className={styles.footBtn}
            loading={pending}
            disabled={!row}
          >
            Stornieren
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <FieldBlock label="Grund" note="Pflichtangabe" error={error} htmlFor={`${formId}-reason`}>
          <textarea
            id={`${formId}-reason`}
            className={clsx(styles.input, styles.textarea, error && styles.inputInvalid)}
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </FieldBlock>
      </form>
    </Dialog>
  );
}
